(() => {
  'use strict';
  if (window.DFCart) return;
  let active;

  function init(widget) {
    if (widget.dataset.dfReady) return;
    if (active) active.destroy();
    widget.dataset.dfReady = 'true';
    const controller = new AbortController();
    const listen = (node, event, handler, options = {}) => node.addEventListener(event, handler, { ...options, signal: controller.signal });
    const drawer = widget.querySelector('[data-df-panel]');
    const scrim = widget.querySelector('[data-df-scrim]');
    const status = widget.querySelector('[data-df-status]');
    const recovery = widget.querySelector('[data-df-recovery]');
    const sectionId = widget.dataset.dfSectionId;
    const root = (widget.dataset.dfRoot || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const pageSectionId = document.querySelector('[data-df-cart-page]')?.dataset.dfPageSection;
    const sections = [...new Set([sectionId, 'cart-icon-bubble', pageSectionId].filter(Boolean))];
    function policyContext() {
      if (widget.dataset.dfSingleCopyContext !== 'true' || window.Shopify?.designMode || window.Shopify?.inspectMode) return false;
      try {
        const url = new URL(location.href);
        if (!/^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8}){0,2}\/)?$/.test(root) || !['https:', 'http:'].includes(url.protocol) ||
            (url.protocol === 'http:' && !['localhost', '127.0.0.1', '[::1]'].includes(url.hostname))) return false;
        let path = url.pathname;
        for (let pass = 0; pass < 3; pass++) { const decoded = decodeURIComponent(path); if (decoded === path) break; path = decoded; }
        if (/[\\\u0000-\u0020\u007f%]/.test(path) || !path.startsWith(root)) return false;
        const privatePath = /^(?:account|accounts|customers|orders?|checkouts?|password|gift[-_]cards?|d|library|downloads?|apps|a|tools|services|challenge|authentication|customer_authentication|identity|auth|login|logout|register|recover|reset|activate|payments?|pay)(?:[/.]|$)/i;
        if (privatePath.test(path.slice(1)) || privatePath.test(path.slice(root.length))) return false;
        const names = new Set();
        if (/%(?![0-9a-f]{2})/i.test(url.search)) return false;
        for (const [key, value] of url.searchParams) {
          const name = key.toLowerCase();
          if (!key || names.has(name) || /[\u0000-\u001f\u007f\[\]]/.test(key + value) || /key|token|nonce|secret|password/i.test(name) ||
              ['section_id', 'sections', 'design_mode', 'shopify_design_mode', 'shopify_editor', 'return_to', 'return_url', 'redirect', 'redirect_url'].includes(name)) return false;
          names.add(name);
        }
        return true;
      } catch { return false; }
    }
    const singleCopy = widget.dataset.dfSingleCopy === 'true' && policyContext();
    let policyReady = !singleCopy;
    let productCache = new Map();
    let queue = Promise.resolve();
    let pending = 0;
    let lastFocused;
    let opened = false;
    let stale = false;
    let latestCart;
    let refreshQueued = false;
    let focusAfterUpdate;
    const externalControls = new Map();
    const requestedQuantities = new Map();
    const background = new Map();

    function readState(content) {
      const cart = JSON.parse(content.dataset.dfCartState);
      if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error(widget.dataset.dfRefreshError);
      return cart;
    }

    function say(message, error = false) {
      status.textContent = message || '';
      status.toggleAttribute('data-error', error);
      document.querySelectorAll('[data-df-page-status]').forEach((node) => { node.textContent = message || ''; });
    }

    function controls() {
      document.querySelectorAll('[data-df-widget] [data-df-add], [data-df-widget] [data-df-qty], [data-df-widget] [data-df-remove], [data-df-widget] [data-df-bump-check], [data-df-widget] [data-df-checkout], [data-df-page-content] [data-df-add], [data-df-page-content] [data-df-qty], [data-df-page-content] [data-df-remove], [data-df-page-content] [data-df-bump-check], [data-df-page-content] [data-df-checkout]').forEach((control) => {
        const disabled = pending > 0 || ((stale || !policyReady) && control.hasAttribute('data-df-checkout'));
        if ('disabled' in control) control.disabled = disabled;
        else control.setAttribute('aria-disabled', String(disabled));
      });
      document.querySelectorAll('[data-df-cart-page] [data-df-retry]').forEach((node) => { node.hidden = !stale; });
      document.querySelectorAll('[data-df-apply-code], [data-df-clear-code], [data-df-coupon]').forEach((node) => { node.disabled = pending > 0; });
      drawer.setAttribute('aria-busy', String(pending > 0));
      syncMembership();
      if (!pending && focusAfterUpdate?.isConnected && (opened || focusAfterUpdate.closest('[data-df-page-content]'))) {
        focusAfterUpdate.focus({ preventScroll: true });
        focusAfterUpdate = null;
      }
    }

    function updateCounts() {
      document.querySelectorAll('[data-df-count]').forEach((node) => { node.textContent = latestCart.item_count; });
      const icon = document.getElementById('cart-icon-bubble');
      let bubble = icon?.querySelector('.cart-count-bubble');
      if (icon && latestCart.item_count > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          const visible = document.createElement('span');
          visible.setAttribute('aria-hidden', 'true');
          const label = document.createElement('span');
          label.className = 'visually-hidden';
          bubble.append(visible, label);
          icon.append(bubble);
        }
        const visible = bubble.querySelector('[aria-hidden="true"]');
        const label = bubble.querySelector('.visually-hidden');
        if (visible) visible.textContent = latestCart.item_count;
        if (label) label.textContent = widget.querySelector('[data-df-content]').dataset.dfCountLabel || latestCart.item_count;
      } else bubble?.remove();
      document.querySelectorAll('[data-df-cart-open]').forEach((node) => {
        node.setAttribute('aria-controls', drawer.id);
        node.setAttribute('aria-expanded', String(opened));
      });
      syncMembership();
    }

    function syncMembership() {
      if (!singleCopy || !latestCart) return;
      const products = new Set(latestCart.items.map(item => String(item.product_id)));
      document.querySelectorAll('[data-df-single-copy-add]').forEach(button => {
        const id = button.dataset.dfProductId || button.closest('[data-df-product-id]')?.dataset.dfProductId;
        if (!id) return;
        const included = products.has(id);
        const wasIncluded = button.dataset.dfSingleCopyIncluded === 'true';
        if (included) { button.disabled = true; button.dataset.dfSingleCopyIncluded = 'true'; if (button.type === 'checkbox') button.checked = false; }
        else if (wasIncluded) {
          button.disabled = button.dataset.dfSingleCopyBaseDisabled === 'true' || externalControls.has(button);
          delete button.dataset.dfSingleCopyIncluded;
        }
        if ((included || wasIncluded) && button.dataset.dfSingleCopyDefaultLabel) button.setAttribute('aria-label', included ? button.dataset.dfSingleCopyLabel || widget.dataset.dfSingleCopyIncluded : button.dataset.dfSingleCopyDefaultLabel);
        const labelRoot = button.tagName === 'INPUT' ? button.closest('label') : button;
        if (included || wasIncluded) labelRoot?.querySelectorAll('[data-df-single-copy-text]').forEach(text => {
          text.textContent = included ? button.dataset.dfSingleCopyLabel || widget.dataset.dfSingleCopyIncluded || text.textContent : text.dataset.dfSingleCopyDefault || text.textContent;
        });
      });
    }

    function policyError() { return new Error(widget.dataset.dfSingleCopyError || widget.dataset.dfRefreshError); }

    async function policyRequest(path, body) {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 15000);
      try {
        const response = await fetch(path, { method: body === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: abort.signal,
          headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
        if (!response.ok) throw policyError();
        return await response.json();
      } finally { clearTimeout(timeout); }
    }

    function policyCart(cart) {
      if (!cart || !Array.isArray(cart.items) || cart.items.length > 1000 || !Number.isInteger(cart.item_count)) throw policyError();
      const keys = new Set();
      for (const item of cart.items) {
        if (!/^\d+$/.test(String(item.product_id)) || typeof item.key !== 'string' || !item.key || keys.has(item.key) ||
            !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.remote === true || item.parent_relationship || item.parent_id ||
            item.item_components?.length || item.components?.length || item.has_components === true || item.requires_components === true ||
            Object.keys(item.properties || {}).some(key => /^_(?:bundle(?:_|$)|parent(?:_|$)|component(?:_|$))/i.test(key)) ||
            (item.quantity_rule && (item.quantity_rule.min > 1 || item.quantity_rule.increment > 1))) throw policyError();
        keys.add(item.key);
      }
      return cart;
    }

    async function normalizeCart() {
      let cart = policyCart(await policyRequest(root + 'cart.js'));
      const products = new Set();
      const updates = {};
      for (const item of cart.items) {
        const id = String(item.product_id);
        if (products.has(id)) updates[item.key] = 0;
        else { products.add(id); if (item.quantity !== 1) updates[item.key] = 1; }
      }
      const changed = Object.keys(updates).length > 0;
      if (changed) {
        await policyRequest(root + 'cart/update.js', { updates });
        cart = policyCart(await policyRequest(root + 'cart.js'));
        const remaining = new Set();
        for (const item of cart.items) {
          const id = String(item.product_id);
          if (item.quantity !== 1 || remaining.has(id)) throw policyError();
          remaining.add(id);
        }
      }
      latestCart = cart;
      return changed;
    }

    function metadataPath(id, hint) {
      if (typeof hint === 'string' && hint) return hint;
      for (const node of document.querySelectorAll('[data-df-product-path]')) {
        const candidate = node.dataset.dfAdd || node.dataset.dfBumpVariant || node.dataset.dfVariantId ||
          (node.matches('[data-df-fbt-choice]') ? node.value : node.querySelector('[name="id"]')?.value);
        if (String(candidate) === String(id)) return node.dataset.dfProductPath;
      }
      return null;
    }

    async function productFor(id, hint) {
      const path = metadataPath(id, hint);
      if (typeof path !== 'string') throw policyError();
      const local = path.startsWith(root + 'products/') ? path.slice(root.length) : path.slice(1);
      if (!/^products\/[a-z0-9][a-z0-9-]*$/.test(local)) throw policyError();
      const url = root + local + '.js';
      if (!productCache.has(url)) productCache.set(url, policyRequest(url));
      const product = await productCache.get(url);
      if (!/^\d+$/.test(String(product?.id)) || !Array.isArray(product.variants) || product.requires_components === true) throw policyError();
      const variant = product.variants.find(variant => String(variant.id) === String(id));
      if (!variant || variant.available !== true || variant.requires_components === true) throw policyError();
      return String(product.id);
    }

    async function uniquePayload(payload, hint) {
      const products = new Set(latestCart.items.map(item => String(item.product_id)));
      if (payload instanceof FormData) {
        if (payload.getAll('id').length !== 1 || [...payload.keys()].some(key => key.startsWith('items['))) throw policyError();
        const id = payload.get('id');
        const product = await productFor(id, hint);
        if (products.has(product)) return null;
        payload.set('quantity', '1');
        return payload;
      }
      if (!Array.isArray(payload?.items) || !payload.items.length || payload.items.length > 20) throw policyError();
      const items = [];
      for (const item of payload.items) {
        if (item.parent_id || item.parent_line_key || !/^\d+$/.test(String(item.id))) throw policyError();
        const product = await productFor(item.id, item.productPath || hint);
        if (products.has(product)) continue;
        products.add(product);
        const { productPath, product_id, ...entry } = item;
        items.push({ ...entry, quantity: 1 });
      }
      return items.length ? { ...payload, items } : null;
    }

    function focusables() {
      return [...drawer.querySelectorAll('a[href],button:not(:disabled),input:not(:disabled),[tabindex="0"]')].filter((node) => !node.closest('[hidden]') && node.getClientRects().length);
    }

    function open({ refresh = true } = {}) {
      if (!opened) {
        lastFocused = document.activeElement;
        opened = true;
        scrim.hidden = false;
        drawer.inert = false;
        drawer.classList.add('df-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('df-cart-is-open');
        let ancestor = widget;
        while (ancestor.parentElement) {
          for (const sibling of ancestor.parentElement.children) {
            if (sibling !== ancestor && sibling instanceof HTMLElement) {
              background.set(sibling, sibling.inert);
              sibling.inert = true;
            }
          }
          ancestor = ancestor.parentElement;
          if (ancestor === document.body) break;
        }
        updateCounts();
        widget.querySelector('[data-df-close]')?.focus({ preventScroll: true });
      }
      if (refresh) requestRefresh();
    }

    function close() {
      if (!opened) return;
      opened = false;
      scrim.hidden = true;
      drawer.inert = true;
      drawer.classList.remove('df-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('df-cart-is-open');
      background.forEach((inert, node) => { node.inert = inert; });
      background.clear();
      updateCounts();
      if (lastFocused?.isConnected) lastFocused.focus({ preventScroll: true });
    }

    function applySection(html, source = 'refresh') {
      const rendered = new DOMParser().parseFromString(html, 'text/html');
      const incoming = rendered.querySelector('[data-df-content]');
      if (!incoming) throw new Error(widget.dataset.dfRefreshError);
      const cart = readState(incoming);
      const previousCart = latestCart;
      const current = widget.querySelector('[data-df-content]');
      const focused = document.activeElement;
      const restoreFocus = current.contains(focused);
      const focusKey = focused?.dataset?.dfQty || focused?.dataset?.dfRemove;
      const wasQuantity = focused?.hasAttribute('data-df-qty');
      const wasBump = focused?.hasAttribute('data-df-bump-check');
      const scroll = current.querySelector('.df-drawer-body')?.scrollTop || 0;
      current.replaceWith(incoming);
      latestCart = cart;
      stale = false;
      recovery.hidden = true;
      const body = incoming.querySelector('.df-drawer-body');
      if (body) body.scrollTop = scroll;
      updateCounts();
      controls();
      if (opened && restoreFocus) {
        let target;
        if (focusKey) target = [...incoming.querySelectorAll(wasQuantity ? '[data-df-qty]' : '[data-df-remove]')].find((node) => (node.dataset.dfQty || node.dataset.dfRemove) === focusKey);
        if (wasBump) target = incoming.querySelector('[data-df-bump-check]');
        focusAfterUpdate = target || incoming.querySelector('[data-df-close]');
        if (!pending) controls();
        else incoming.querySelector('[data-df-close]')?.focus({ preventScroll: true });
      }
      const action = ['product', 'cross-sell', 'addon', 'frequently-bought', 'free-campaign'].includes(source) ? 'add' : ['remove', 'remove-addon'].includes(source) ? 'remove' : source === 'quantity' ? 'change' : 'refresh';
      widget.dispatchEvent(new CustomEvent('df:cart-updated', { bubbles: true, detail: { cart, previousCart, count: cart.item_count, source, action, origin: 'df-cart' } }));
    }

    function syncBubble(html) {
      if (!html) return;
      const incoming = new DOMParser().parseFromString(html, 'text/html');
      const bubble = document.getElementById('cart-icon-bubble');
      const content = incoming.querySelector('.shopify-section');
      if (bubble && content) bubble.innerHTML = content.innerHTML;
    }

    function applyPage(html) {
      if (!pageSectionId) return;
      const rendered = new DOMParser().parseFromString(html, 'text/html');
      const incoming = rendered.querySelector('[data-df-page-content]');
      const current = document.querySelector('[data-df-page-content]');
      if (!incoming || !current) throw new Error(widget.dataset.dfRefreshError);
      const focused = document.activeElement;
      const restore = current.contains(focused);
      const key = focused?.dataset?.dfQty || focused?.dataset?.dfRemove;
      const isQuantity = focused?.hasAttribute('data-df-qty');
      const isBump = focused?.hasAttribute('data-df-bump-check');
      current.replaceWith(incoming);
      if (restore && !opened) {
        const selector = isQuantity ? '[data-df-qty]' : '[data-df-remove]';
        let target = key && [...incoming.querySelectorAll(selector)].find((node) => (node.dataset.dfQty || node.dataset.dfRemove) === key);
        if (isBump) target = incoming.querySelector('[data-df-bump-check]');
        if (!target) { target = incoming.querySelector('h1'); target?.setAttribute('tabindex', '-1'); }
        focusAfterUpdate = target;
      }
      controls();
    }

    async function renderSections(data, source) {
      if (!data?.[sectionId] || (pageSectionId && !data?.[pageSectionId])) { await refresh(source); return; }
      applySection(data[sectionId], source);
      if (pageSectionId) applyPage(data[pageSectionId]);
      syncBubble(data['cart-icon-bubble']);
    }

    async function refresh(source = 'refresh') {
      try {
        const url = new URL(location.href);
        url.searchParams.delete('section_id');
        if (pageSectionId) url.searchParams.set('sections', sections.join(','));
        else url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' }, cache: 'no-store' });
        if (!response.ok) throw new Error(widget.dataset.dfRefreshError);
        if (pageSectionId) {
          const data = await response.json();
          if (!data[sectionId] || !data[pageSectionId]) throw new Error(widget.dataset.dfRefreshError);
          applySection(data[sectionId], source); applyPage(data[pageSectionId]); syncBubble(data['cart-icon-bubble']);
        } else applySection(await response.text(), source);
      } catch {
        stale = true;
        throw new Error(widget.dataset.dfRefreshError);
      }
    }

    async function mutate(endpoint, payload, source, productPath) {
      if (singleCopy && endpoint === 'cart/add.js') {
        payload = await uniquePayload(payload, productPath);
        if (!payload) { await refresh('single-copy'); say(widget.dataset.dfSingleCopyIncluded); return { skipped: true }; }
      }
      let body;
      const headers = { Accept: 'application/json' };
      if (payload instanceof FormData) {
        body = payload;
        body.set('sections', sections.join(','));
        body.set('sections_url', location.pathname);
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ ...payload, sections, sections_url: location.pathname });
      }
      let response;
      let data;
      try {
        response = await fetch(root + endpoint, { method: 'POST', credentials: 'same-origin', headers, body });
        data = await response.json();
      } catch {
        stale = true;
        throw new Error(widget.dataset.dfRefreshError);
      }
      if (!response.ok || data.status >= 400) {
        try { await refresh('recovery'); } catch { stale = true; }
        throw new Error(data.description || data.message || widget.dataset.dfError);
      }
      try {
        await renderSections(data.sections, source);
      } catch {
        stale = true;
        throw new Error(widget.dataset.dfRefreshError);
      }
      say(widget.dataset.dfUpdated);
    }

    function enqueue(operation, { show = false, form } = {}) {
      pending += 1;
      if (show) open({ refresh: false });
      if (form) {
        const button = form.querySelector('[type="submit"]');
        if (button && !externalControls.has(button)) {
          externalControls.set(button, button.disabled);
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }
      }
      controls();
      say(widget.dataset.dfLoading);
      const task = queue.then(async () => {
        const perform = async () => {
          productCache = new Map();
          if (singleCopy) await normalizeCart();
          if (stale) await refresh('recovery');
          const result = await operation();
          if (singleCopy && await normalizeCart()) await refresh('single-copy');
          policyReady = true;
          return result;
        };
        try {
          if (!singleCopy) return await perform();
          if (typeof window.navigator?.locks?.request !== 'function') throw policyError();
          return await window.navigator.locks.request('df-free-funnel-v1', perform);
        } catch (error) {
          if (singleCopy) {
            policyReady = false;
            try { await refresh('recovery'); } catch {}
            stale = true;
          }
          say(error.message || widget.dataset.dfError, true);
          recovery.hidden = false;
          throw error;
        } finally {
          pending -= 1;
          if (!pending) {
            externalControls.forEach((disabled, button) => {
              button.disabled = disabled;
              button.removeAttribute('aria-busy');
            });
            externalControls.clear();
          }
          controls();
        }
      });
      queue = task.catch(() => {});
      return task;
    }

    function requestRefresh() {
      if (refreshQueued) return;
      refreshQueued = true;
      enqueue(async () => { await refresh(); say(''); }).catch(() => {}).finally(() => { refreshQueued = false; });
    }

    function findLine(snapshot) {
      if (!snapshot) return null;
      const exact = latestCart.items.find((item) => item.key === snapshot.key);
      if (exact) return exact;
      const signature = (item) => JSON.stringify([item.variant_id || item.id, Object.entries(item.properties || {}).sort(), item.selling_plan_allocation?.selling_plan?.id || null]);
      const matches = latestCart.items.filter((item) => signature(item) === signature(snapshot));
      if (matches.length === 1) return matches[0];
      throw new Error(widget.dataset.dfUnavailableError);
    }

    function changeLine(key, quantity) {
      const snapshot = latestCart.items.find((item) => item.key === key);
      if (requestedQuantities.get(key) === quantity) return;
      if (!requestedQuantities.has(key) && snapshot?.quantity === quantity) return;
      requestedQuantities.set(key, quantity);
      enqueue(async () => {
        const line = findLine(snapshot);
        if (!line) throw new Error(widget.dataset.dfUnavailableError);
        await mutate('cart/change.js', { id: line.key, quantity: singleCopy && quantity ? 1 : quantity }, quantity ? 'quantity' : 'remove');
      }).catch(() => {}).finally(() => {
        if (requestedQuantities.get(key) === quantity) requestedQuantities.delete(key);
      });
    }

    listen(document, 'click', (event) => {
      const opener = event.target.closest('[data-df-cart-open], [data-df-open]');
      if (opener) { event.preventDefault(); open(); return; }
      if (!widget.contains(event.target) && !event.target.closest('[data-df-page-content]')) return;
      if (event.target.closest('[data-df-close], [data-df-scrim]')) { close(); return; }
      if (event.target.closest('[data-df-retry]')) { requestRefresh(); return; }
      const applyCode = event.target.closest('[data-df-apply-code], [data-df-clear-code]');
      if (applyCode) {
        const input = applyCode.closest('.df-coupon-controls')?.querySelector('[data-df-coupon]');
        if (!input) return;
        const clearing = applyCode.hasAttribute('data-df-clear-code');
        const code = clearing ? '' : input.value.trim();
        if (!clearing && !code) { say(input.dataset.dfRequired, true); input.focus(); return; }
        enqueue(async () => {
          await mutate('cart/update.js', { discount: code }, 'discount');
          const reviews = document.querySelectorAll('[data-df-code-review]');
          reviews.forEach((review) => { review.hidden = false; });
          if (reviews.length) say(reviews[0].textContent);
        }).catch(() => {});
        return;
      }
      const remove = event.target.closest('[data-df-remove]');
      if (remove) { event.preventDefault(); changeLine(remove.dataset.dfRemove, 0); return; }
      const add = event.target.closest('[data-df-add]');
      if (add) {
        const variant = add.dataset.dfAdd;
        enqueue(async () => {
          if (!latestCart.items.some((item) => String(item.variant_id || item.id) === variant)) await mutate('cart/add.js', { items: [{ id: variant, quantity: 1 }] }, 'cross-sell', add.dataset.dfProductPath);
        }).catch(() => {});
      }
    });

    listen(document, 'change', (event) => {
      if (!widget.contains(event.target) && !event.target.closest('[data-df-page-content]')) return;
      const input = event.target.closest('[data-df-qty]');
      if (input) {
        const value = Number(input.value);
        if (!Number.isSafeInteger(value) || !input.checkValidity()) { say(widget.dataset.dfQuantityError, true); input.focus(); return; }
        changeLine(input.dataset.dfQty, value);
        return;
      }
      const checkbox = event.target.closest('[data-df-bump-check]');
      if (!checkbox) return;
      const variant = checkbox.closest('[data-df-bump-box]').dataset.dfBumpVariant;
      const selected = checkbox.checked;
      enqueue(async () => {
        const matching = latestCart.items.filter((item) => String(item.variant_id || item.id) === variant);
        if (selected && !matching.length) await mutate('cart/add.js', { items: [{ id: variant, quantity: 1, properties: { _df_offer: 'cart-addon' } }] }, 'addon', checkbox.closest('[data-df-bump-box]').dataset.dfProductPath);
        if (!selected) {
          const owned = matching.filter((item) => item.properties?._df_offer === 'cart-addon');
          if (owned.length) await mutate('cart/update.js', { updates: Object.fromEntries(owned.map((item) => [item.key, 0])) }, 'remove-addon');
        }
      }).catch(() => {});
    });

    listen(document, 'submit', (event) => {
      const form = event.target;
      if (form.matches('form[data-df-fbt-form]')) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (form.dataset.dfSubmitting) return;
        const selected = [...form.querySelectorAll('[data-df-fbt-choice]:checked')];
        const formStatus = form.querySelector('[data-df-fbt-status]');
        if (!selected.length) { if (formStatus) formStatus.textContent = form.dataset.dfRequired; return; }
        form.dataset.dfSubmitting = 'true';
        addItems(selected.map((node) => ({ id: node.value, quantity: 1, productPath: node.dataset.dfProductPath })), { source: 'frequently-bought', show: true, form }).then((result) => {
          selected.forEach((node) => { node.checked = false; });
          if (formStatus) formStatus.textContent = result?.skipped ? widget.dataset.dfSingleCopyIncluded : form.dataset.dfAdded;
        }).catch((error) => { if (formStatus) formStatus.textContent = error.message; }).finally(() => { delete form.dataset.dfSubmitting; });
        return;
      }
      if (!form.matches('form[data-type="add-to-cart-form"], form[data-df-product-form]')) return;
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (form.dataset.dfSubmitting) return;
      if (!form.checkValidity()) { form.reportValidity(); return; }
      const payload = new FormData(form);
      const quickAdd = form.closest('quick-add-modal');
      const quickAddOpener = quickAdd?.openedBy;
      if (typeof quickAdd?.hide === 'function') quickAdd.hide(true);
      form.dataset.dfSubmitting = 'true';
      enqueue(() => mutate('cart/add.js', payload, 'product', form.dataset.dfProductPath), { show: true, form }).catch(() => {}).finally(() => { delete form.dataset.dfSubmitting; });
      if (quickAddOpener) lastFocused = quickAddOpener;
    }, { capture: true });

    listen(document, 'keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-df-coupon]')) {
        event.preventDefault();
        event.target.closest('.df-coupon-controls')?.querySelector('[data-df-apply-code]')?.click();
        return;
      }
      if (!opened) return;
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const nodes = focusables();
      if (!nodes.length) { event.preventDefault(); drawer.focus(); return; }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    });
    listen(document, 'focusin', (event) => {
      if (opened && !drawer.contains(event.target)) (focusables()[0] || drawer).focus({ preventScroll: true });
    });
    listen(document, 'df:open-cart', () => open());
    listen(document, 'df:refresh-cart', requestRefresh);
    listen(window, 'pageshow', (event) => { if (event.persisted) requestRefresh(); });
    let unsubscribe;
    let unsubscribeVariant;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'df-cart') requestRefresh();
      });
      if (PUB_SUB_EVENTS.variantChange) unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!singleCopy || !event.data?.html) return;
        const id = 'ProductSubmitButton-' + event.data.sectionId;
        const button = document.getElementById(id);
        const incoming = event.data.html.getElementById(id);
        if (button && incoming) {
          button.dataset.dfSingleCopyBaseDisabled = incoming.dataset.dfSingleCopyBaseDisabled || 'false';
          const label = button.querySelector('[data-df-single-copy-text]');
          const freshLabel = incoming.querySelector('[data-df-single-copy-text]');
          if (label && freshLabel) label.dataset.dfSingleCopyDefault = freshLabel.dataset.dfSingleCopyDefault || freshLabel.textContent;
        }
        syncMembership();
      });
    }
    latestCart = readState(widget.querySelector('[data-df-content]'));
    updateCounts();
    function addItems(items, options = {}) {
      if (!Array.isArray(items) || !items.length || items.length > 20 || items.some((item) => !/^\d+$/.test(String(item.id)) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 1000)) return Promise.reject(new Error(widget.dataset.dfQuantityError));
      const combined = new Map();
      items.forEach((item) => combined.set(String(item.id), Math.max(combined.get(String(item.id)) || 0, item.quantity)));
      const additions = singleCopy ? [...combined].map(([id]) => ({ ...items.find(item => String(item.id) === id), id, quantity: 1 })) : [...combined].map(([id, quantity]) => ({ id, quantity }));
      return enqueue(() => mutate('cart/add.js', { items: additions }, options.source || 'frequently-bought'), options);
    }
    function runExclusive(operation) {
      if (typeof operation !== 'function') return Promise.reject(new Error(widget.dataset.dfError));
      return enqueue(async () => {
        let result;
        try { result = await operation(); }
        finally { await refresh(result?.added === true ? 'free-campaign' : 'refresh'); }
        return result;
      });
    }
    active = { open, close, addItems, runExclusive, singleCopyManaged: singleCopy && typeof window.navigator?.locks?.request === 'function', refresh: requestRefresh, idle: () => queue, destroy() { close(); controller.abort(); unsubscribe?.(); unsubscribeVariant?.(); delete widget.dataset.dfReady; } };
    if (singleCopy) requestRefresh();
  }

  function boot() {
    const widget = document.querySelector('[data-df-widget]');
    if (widget) init(widget);
  }
  window.DFCart = { open: () => active?.open(), close: () => active?.close(), addItems: (items, options) => active?.addItems(items, options) || Promise.reject(new Error('Cart is not ready.')), runExclusive: (operation) => active?.runExclusive(operation) || Promise.reject(new Error('Cart is not ready.')), get singleCopyManaged() { return active?.singleCopyManaged === true; }, refresh: () => active?.refresh(), idle: () => active?.idle() || Promise.resolve() };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('[data-df-widget]')) { active?.destroy(); active = null; }
  });
})();
